import { SRGBColorSpace, WebGLRenderer } from 'three';

import { DESIGN_PACKAGE_EXPOSURE, DESIGN_PACKAGE_LOOK, DESIGN_PACKAGE_TONE_MAPPING } from '../../../../constants/design-package-look';
import type { QualitySettings } from '../../../../hooks/use-three-kitchen/quality';
import { resolveGpuPreference, type GpuPreference } from '../../../utils/gpu-detector';

export interface CreateRendererParams {
	qualitySettings: QualitySettings;
	xrEnabled: boolean;
	gpuPreference: GpuPreference;
	/** Override quality-derived antialias (mobile memory budget). */
	antialiasOverride?: boolean;
	/** Override quality-derived pixelRatio (mobile memory budget). */
	pixelRatioOverride?: number;
}

/**
 * Create the WebGL renderer with quality settings
 */
export const createRendererCfn = (_self: unknown, params: CreateRendererParams): WebGLRenderer => {
	const { qualitySettings, xrEnabled, gpuPreference, antialiasOverride, pixelRatioOverride } = params;

	const gpuResult = resolveGpuPreference(gpuPreference);
	const renderer = new WebGLRenderer({
		antialias: antialiasOverride ?? qualitySettings.antialias,
		powerPreference: gpuResult.powerPreference,
		failIfMajorPerformanceCaveat: false,
		alpha: true,
		stencil: false,
		depth: true,
		// Keeps the last frame readable for the AI-rendering live preview blit (drawImage from an independent rAF)
		preserveDrawingBuffer: true,
		// Enable logarithmic depth buffer for high quality (8-10) to prevent z-fighting
		logarithmicDepthBuffer: qualitySettings.logarithmicDepthBuffer
	});

	renderer.setPixelRatio(pixelRatioOverride ?? qualitySettings.pixelRatio);
	renderer.setClearColor(0x000000);
	renderer.outputColorSpace = SRGBColorSpace;
	renderer.toneMapping = DESIGN_PACKAGE_LOOK ? DESIGN_PACKAGE_TONE_MAPPING : qualitySettings.toneMapping;
	renderer.toneMappingExposure = DESIGN_PACKAGE_LOOK ? DESIGN_PACKAGE_EXPOSURE : 1.2;
	renderer.xr.enabled = xrEnabled;
	renderer.shadowMap.enabled = qualitySettings.shadowMapEnabled;
	renderer.shadowMap.type = qualitySettings.shadowMapType;
	renderer.shadowMap.autoUpdate = false;

	return renderer;
};
